import { logService } from 'npo-common';
import { IApiResponse } from 'src/common/ApiResponse';
import { httpRequest } from 'src/common/HttpRequest';
import { getBrowserSupportedLanguages, getSupportedLanguages } from 'src/components/utilities/Localization/Utils';
import { initialState } from 'src/state/localization/reducer';
import { ILocale, ILocalizationResolver, ILocalizationResolverReason } from 'src/state/localization/types';
import { store } from 'src/state/reduxStore';

const LOC_V2_QUERY_PARAM = 'locv2';

export class LocaleService {
  private fallbackLocale = 'en-us';
  private currentLocale!: string | undefined;
  private localeContentDictionary: { [key: string]: ILocale } = {};
  private localizationFeatureOn = this.isLocalizationEnabled();

  constructor() {
    if (!this.localizationFeatureOn) {
      this.currentLocale = 'en-us';
    }
  }

  public isLocalizationEnabled(): boolean {
    return process.env.REACT_APP_LOCALIZATION_ENABLED === '1';
  }

  private fetchLocalLocalizationData(locale: string): any {
    // We can't use require since it's a dynamic import so we use a synchronous request instead
    const request = new XMLHttpRequest();
    request.open('GET', `${process.env.PUBLIC_URL}/cdn/localization/${locale}.json`, false);
    request.send(null);

    return JSON.parse(request.responseText);
  }

  private async fetchLocalSignupLocalizationData(locale: string): Promise<any> {
    try {
      const response = await fetch(`${process.env.PUBLIC_URL}/signup-localization/${locale}.json`);

      if (response.ok) {
        return await response.json();
      }

      throw new Error(`Failed to fetch local signup localization: ${response.status}`);
    } catch (error) {
      logService.logError(`Failed to fetch local signup localization for ${locale}`, { error });

      throw error;
    }
  }

  public getLocaleContent(locale?: string): any {
    if (this.localizationFeatureOn) {
      const contentRetrieved = this.localeContentDictionary[this.currentLocale ?? locale ?? this.fallbackLocale];

      return contentRetrieved !== undefined && contentRetrieved.content;
    } else {
      locale = locale || this.currentLocale || this.fallbackLocale;
      return this.fetchLocalLocalizationData(locale);
    }
  }

  public getCategoryContent(category: string, locale?: string): { [key: string]: any } {
    const content = this.getLocaleContent(locale);

    // On a cold load the localization content may not be hydrated into the dictionary yet,
    // in which case getLocaleContent returns a falsy value. Skip logging a "category not found"
    // error in that case to avoid false positives; components re-render once content is loaded.
    if (!content) {
      return undefined as unknown as { [key: string]: any };
    }

    const categoryContent = content[category];
    if (categoryContent === undefined) {
      const effectiveLocale = this.currentLocale ?? locale ?? this.fallbackLocale;
      logService.logError(`Category ${category} not found in locale ${effectiveLocale}`);
    }

    return categoryContent;
  }

  public getContent(category: string, id: string, locale?: string): { [key: string]: any } {
    const categoryContent = this.getCategoryContent(category, locale);
    if (categoryContent === undefined) {
      return {};
    }

    const text = categoryContent[id];
    return text;
  }

  public getText(category: string, id: string, locale?: string): string | undefined {
    const categoryContent = this.getCategoryContent(category, locale);
    const text = categoryContent?.[id];

    if (text !== undefined) {
      return text;
    }

    // Fallback to en-us when the effective locale is missing the text
    const effectiveLocale = locale || this.currentLocale || this.fallbackLocale;
    if (effectiveLocale !== this.fallbackLocale) {
      return this.getFallbackCategoryContent(category)?.[id];
    }

    return text;
  }

  private getFallbackCategoryContent(category: string): { [key: string]: any } | undefined {
    let content: any;

    if (this.localizationFeatureOn) {
      const fallbackEntry = this.localeContentDictionary[this.fallbackLocale];
      if (fallbackEntry) {
        content = fallbackEntry.content;
      } else {
        try {
          const localContent = this.fetchLocalLocalizationData(this.fallbackLocale);
          this.localeContentDictionary[this.fallbackLocale] = { key: this.fallbackLocale, content: localContent };
          content = localContent;
        } catch {
          return undefined;
        }
      }
    } else {
      if (!this.localeContentDictionary[this.fallbackLocale]) {
        const localContent = this.fetchLocalLocalizationData(this.fallbackLocale);
        this.localeContentDictionary[this.fallbackLocale] = { key: this.fallbackLocale, content: localContent };
      }
      content = this.localeContentDictionary[this.fallbackLocale].content;
    }

    return content?.[category];
  }

  public getlocale(): string | undefined {
    return this.currentLocale;
  }

  public getDateString(date: string): string {
    return new Date(date).toLocaleString(this.currentLocale, { year: 'numeric', month: 'numeric', day: 'numeric' });
  }

  public fetchDataFromLocalStorage(locale: string): { [key: string]: any } {
    const localDictionaryResponse = this.localeContentDictionary[locale];

    this.currentLocale = locale;
    return localDictionaryResponse.content;
  }

  public async fetchDataFromCDN(locale?: string): Promise<IApiResponse<{ [key: string]: any }>> {
    const locBaseUrl = this.getLocalizationBaseUrl();
    try {
      const localDictionaryResponse = await httpRequest(`${locBaseUrl}/${locale}.json`);

      if (localDictionaryResponse.status === 200) {
        this.currentLocale = locale;
        return {
          status: localDictionaryResponse.status,
          value: await localDictionaryResponse.json()
        };
      }

      logService.logError(
        `CDN fetch failed, falling back to local file.`,
        { locale, status: localDictionaryResponse.status },
        { console: true }
      );
    } catch (error) {
      logService.logError(`CDN request error, falling back to local file.`, { locale, error }, { console: true });
    }

    // Fallback to local file
    try {
      const fallbackContent = this.fetchLocalLocalizationData(locale!);
      this.currentLocale = locale;
      logService.logInfo(`Successfully loaded localization from local fallback for ${locale}`);
      return {
        status: 200,
        value: fallbackContent
      };
    } catch (fallbackError) {
      logService.logError(`Failed to load localization from local fallback`, { locale, fallbackError });
      return {
        status: 500,
        value: undefined
      };
    }
  }

  public async fetchDataFromSignupCDN(locale?: string): Promise<IApiResponse<{ [key: string]: any }>> {
    const signupLocBaseUrl = this.getSignupLocalizationBaseUrl();

    try {
      const regulatoryBodiesResponse = await httpRequest(`${signupLocBaseUrl}/${locale}.json`);

      this.currentLocale = locale;
      if (regulatoryBodiesResponse.status === 200) {
        const regulatoryBodiesResponseJson = await regulatoryBodiesResponse.json();
        return {
          status: regulatoryBodiesResponse.status,
          value: {
            RegulatoryBodies: {
              errorMessages: regulatoryBodiesResponseJson.OrgInfoForm.errorMessages,
              identifiers:
                regulatoryBodiesResponseJson.OrgInfoForm.registration[
                  process.env.REACT_APP_IDENTIFIERS_PATH || 'identifiers'
                ]
            },
            JobTitles: regulatoryBodiesResponseJson.WelcomePolicies.jobTitles,
            Registration: regulatoryBodiesResponseJson.OrgInfoForm.registration
          }
        };
      }

      logService.logError(
        `Signup CDN fetch failed, falling back to local file.`,
        { locale, status: regulatoryBodiesResponse.status },
        { console: true }
      );
    } catch (error) {
      logService.logError(
        `Signup CDN request error, falling back to local file.`,
        { locale, error },
        { console: true }
      );
    }

    // Fallback to local file
    try {
      const fallbackContent = await this.fetchLocalSignupLocalizationData(locale || this.fallbackLocale);
      this.currentLocale = locale;
      logService.logInfo(`Successfully loaded signup localization from local fallback for ${locale}`);
      return {
        status: 200,
        value: {
          RegulatoryBodies: {
            errorMessages: fallbackContent.OrgInfoForm.errorMessages,
            identifiers:
              fallbackContent.OrgInfoForm.registration[process.env.REACT_APP_IDENTIFIERS_PATH || 'identifiers']
          },
          JobTitles: fallbackContent.WelcomePolicies.jobTitles,
          Registration: fallbackContent.OrgInfoForm.registration
        }
      };
    } catch (fallbackError) {
      logService.logError(`Failed to load signup localization from local fallback`, { locale, fallbackError });
      return {
        status: 500,
        value: undefined
      };
    }
  }

  // gets the local json file from the cdn folder
  public getFallbackData(requestedLocale?: string) {
    const locale = requestedLocale || this.currentLocale || this.fallbackLocale;
    const fallbackContent = this.fetchLocalLocalizationData(locale);
    return { key: locale, content: fallbackContent } as ILocale;
  }

  public addContentToDictionary(value: ILocale): boolean {
    this.localeContentDictionary[value.key] = { key: value.key, content: value.content };

    this.currentLocale = value.key;
    return this.localeContentDictionary[value.key] !== null;
  }

  public evaluateLocaleToFetch(localeToEvaluate?: string): ILocalizationResolver {
    if (localeToEvaluate !== undefined) {
      const isSupportedLanguage = getSupportedLanguages().includes(localeToEvaluate.toLowerCase());

      if (isSupportedLanguage) {
        return this.resolveWithLocaleToFetch(localeToEvaluate.toLowerCase());
      }
    }

    // always a subset of languages the app supports in user's preference order. Pick the first one to resolve to.
    const browserSupportedLanguages = getBrowserSupportedLanguages();
    if (browserSupportedLanguages.length > 0) {
      return this.resolveWithLocaleToFetch(browserSupportedLanguages[0].toLowerCase());
    }

    return {
      resolvedReason: ILocalizationResolverReason.None,
      languageToFetch: initialState.defaultAppLanguage.toLowerCase()
    };
  }

  private resolveWithLocaleToFetch(localeToEvaluate: string) {
    if (this.isInLocalStorage(localeToEvaluate)) {
      return {
        resolvedReason: ILocalizationResolverReason.FetchFromLocalStorage,
        languageToFetch: localeToEvaluate.toLowerCase()
      };
    } else {
      return {
        resolvedReason: ILocalizationResolverReason.FetchFromCDN,
        languageToFetch: localeToEvaluate.toLowerCase()
      };
    }
  }

  public purgeLocalStorage(): void {
    this.localeContentDictionary = {};
  }

  private isInLocalStorage(locale: string): boolean {
    const value = this.localeContentDictionary[locale];

    return value !== undefined;
  }

  private getLocalizationBaseUrl() {
    const state = store.getState();
    const v2QueryParam = window.location.search.includes(`${LOC_V2_QUERY_PARAM}=true`);
    const useLocV2 = state.flags.LocalizationV2 || v2QueryParam;
    const baseUrl = useLocV2
      ? process.env.REACT_APP_LOCALIZATION_CONTENT_V2_URL
      : process.env.REACT_APP_LOCALIZATION_CONTENT_URL;

    console.debug(`loc${useLocV2 ? 'v2' : 'v1'}`, {
      v2QueryParam,
      v2Flag: state.flags.LocalizationV2,
      baseUrl
    });

    return baseUrl;
  }

  private getSignupLocalizationBaseUrl() {
    const state = store.getState();
    const v2QueryParam = window.location.search.includes(`${LOC_V2_QUERY_PARAM}=true`);
    const useLocV2 = state.flags.LocalizationV2 || v2QueryParam;
    const baseUrl = useLocV2
      ? process.env.REACT_APP_SIGNUP_LOCALIZATION_V2_URL
      : process.env.REACT_APP_SIGNUP_LOCALIZATION_URL;

    console.debug(`signupLoc${useLocV2 ? 'v2' : 'v1'}`, {
      v2QueryParam,
      v2Flag: state.flags.LocalizationV2,
      baseUrl
    });

    return baseUrl;
  }
}

const localeService = new LocaleService();
export default localeService;

